import groupBy from 'lodash/groupBy';
import type {
    CompareWarningsTypesPayload,
    WarningResult,
    LogLevelsItems,
    CompareRulesItems,
    GetFileComparisonResponse,
    GetWarningsTypesPayload,
    GetWarningsTypesResponse,
    WarningRow,
    PSOLogScenario
} from './types';

export function mapPSOLogsScenarioItem(i: PSOLogScenario) {
    return {
        idx: i.scenarioId,
        scenarioName: i.scenarioName,
        state: i.state,
    };
}

export const mapComparedWarningsTypes = (i: CompareWarningsTypesPayload): GetFileComparisonResponse | undefined => {
    if (i.compareWarningsTypes.type === 'id') return;

    const warnings: WarningResult[] = [];
    let logLevels: LogLevelsItems = {};
    let compareRules: CompareRulesItems = {};

    const rows: Record<string, string | null>[] = JSON.parse(i.compareWarningsTypes.value) || [];

    for (const item of rows) {
        // group by prefix
        const groups = groupBy(Object.entries(item), ([prop]) => {
            const parts = prop.split('_');
            parts.pop();
            return parts.join('_');
        });

        // Delete suffix for each prop in object - severity_123 -> severity, type_123 -> type, count_123 -> count
        const result: WarningResult = {};
        for (const [key, data] of Object.entries(groups)) {
            result[key] = Object.fromEntries(
                (data as Array<[string, string | null]>).map(([keyId, value]) => [keyId.split('_').pop()!, value])
            );
        }

        // Calculate number of warning types for each severity
        if (result.severity) {
            for (const [scenarioId, severity] of Object.entries(result.severity)) {
                if (severity !== null) {
                    if (!logLevels[severity]) logLevels[severity] = {};
                    logLevels[severity][scenarioId] = (logLevels[severity][scenarioId] ?? 0) + 1;
                }
            }
        }

        if (result.compare) {
            for (const [, compareRule] of Object.entries(result.compare)) {
                if (compareRule !== null) {
                    compareRules[compareRule] = (compareRules[compareRule] ?? 0) + 1;
                }
            }
        }

        warnings.push(result);
    }

    return {
        warnings: { items: warnings },
        logLevels: { items: logLevels, selected: Object.keys(logLevels) },
        compareRules: { items: compareRules, selected: Object.keys(compareRules) },
    };
};

export function mapWarningsTypes(result: GetWarningsTypesPayload, scenarioId: number): GetWarningsTypesResponse | undefined {
    if (result.getWarningsTypes.type === "id") return;

    let warnings: WarningRow[] = (JSON.parse(result.getWarningsTypes.value) as WarningRow[]).map(
        (x) => ({
            ...x,
            count: x.count,
            severity: x.severity,
        })
    );

    let levels: Record<string, number> = Object.fromEntries(
        Object.entries(groupBy(warnings, "severity")).map(([level, items]) => [level, items.length])
    );

    const wrappedWarnings: Record<string, Record<string, string>>[] = warnings.map((x) =>
        Object.fromEntries(
            Object.entries(x).map(([key, value]) => [key, { [scenarioId]: value }])
        )
    );

    const wrappedLevels: Record<string, Record<string, number>> = Object.fromEntries(
        Object.entries(levels).map(([key, value]) => [key, { [scenarioId]: value }])
    );

    return {
        warnings: { items: wrappedWarnings },
        logLevels: {
            items: wrappedLevels,
            selected: Object.keys(wrappedLevels),
        },
    };
};